import { IAvailableRolelist, IUserData } from 'models/IUserData'
import {
	ErrorState,
	UpdateUserPayload,
	UserFormState,
} from '../types/settingUserTypes'

type ErrorResponseData = {
	password?: string[]
	username?: string[]
	email?: string[]
}

type ErrorWithResponse = {
	response?: {
		data?: ErrorResponseData
	}
}

type AvatarErrorResponse = {
	avatar?: string | string[]
}

type ErrorWithAvatarResponse = {
	response?: {
		data?: AvatarErrorResponse
	}
}

export const createBlankState = (): UserFormState => ({
	role: -1,
	role_title: '',
	id: null,
	username: '',
	fullname: '',
	lastname: '',
	firstname: '',
	password: '',
	position: '',
	email: '',
	mobile_phone: '',
	superuser: false,
	avatar: null,
})

export const createDefaultErrorState = (): ErrorState => ({
	fullname: false,
	username: false,
	phoneNum: false,
	email: false,
	position: false,
})

export const mapUserResponseToFormState = (data: IUserData): UserFormState => ({
	role: data.role ?? data.role_title ?? -1,
	role_title: data.role_title ?? '',
	id: data.id ?? null,
	username: data.username ?? '',
	fullname: `${data.lastname ?? ''} ${data.firstname ?? ''}`.trim(),
	lastname: data.lastname ?? '',
	firstname: data.firstname ?? '',
	password: '',
	position: data.position ?? '',
	email: data.email ?? '',
	mobile_phone: getNormalizedPhone(data.mobile_phone ?? ''),
	superuser: data.superuser ?? false,
	avatar: (data.avatar as string | undefined) ?? null,
})

export const getUserStateDiffKey = (userData: UserFormState) => {
	const rest = { ...userData }
	delete rest.password
	return JSON.stringify(rest)
}

export const isUserStateDirty = (
	userData: UserFormState,
	initialState: UserFormState,
) => getUserStateDiffKey(userData) !== getUserStateDiffKey(initialState)

export const getFullnameParts = (fullname: string) => {
	const parts = fullname.trim().split(/\s+/)
	return {
		lastname: parts[0] ?? '',
		firstname: parts[1] ?? '',
	}
}

export const getNormalizedPhone = (value: string) => {
	const digits = value.replace(/\D/g, '')
	return digits ? `+${digits}` : ''
}

export const createUpdateUserPayload = (
	userData: UserFormState,
	newPassword: string,
	availableRoles: IAvailableRolelist[],
): UpdateUserPayload => {
	const { lastname, firstname } = getFullnameParts(userData.fullname)
	const username = getNormalizedPhone(userData.mobile_phone)
	const baseRole =
		typeof userData.role === 'string' || userData.role === -1
			? null
			: userData.role
	const updateData: UpdateUserPayload = {
		lastname,
		firstname,
		role: baseRole,
		username,
		email: userData.email,
		position: userData.position,
		mobile_phone: getNormalizedPhone(userData.mobile_phone),
		password: newPassword,
	}

	if (typeof userData.role === 'string') {
		const matchedRole = availableRoles.find(
			role => role.title === userData.role,
		)
		if (matchedRole) {
			updateData.role = matchedRole.id
		}
	}
	if (!updateData.username) updateData.username = updateData.email

	return updateData
}

export const getErrorInfoFromResponse = (error: unknown) => {
	if (!error || typeof error !== 'object') {
		return { message: '', isUsernameError: false }
	}
	const maybeError = error as ErrorWithResponse
	const data = maybeError.response?.data
	if (!data) return { message: '', isUsernameError: false }
	if (data.password && data.password.length > 0) {
		return { message: data.password[0], isUsernameError: false }
	}
	if (data.username && data.username.length > 0) {
		return {
			message: 'Пользователь с таким номером телефона уже существует',
			isUsernameError: true,
		}
	}
	if (data.email && data.email.length > 0) {
		return { message: data.email[0], isUsernameError: false }
	}
	return { message: '', isUsernameError: false }
}

export const getAvatarErrorMessage = (error: unknown) => {
	if (!error || typeof error !== 'object') return ''
	const maybeError = error as ErrorWithAvatarResponse
	const data = maybeError.response?.data
	if (!data || !data.avatar) return ''
	if (Array.isArray(data.avatar)) return data.avatar[0] ?? ''
	return data.avatar
}

export const validateFullname = (fullname: string) =>
	fullname.trim().split(/\s+/).length !== 2

export const validateEmail = (email: string) => {
	const emailValidate = email.match(
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
	)
	return !emailValidate
}

export const validatePhone = (phone: string) => phone.length <= 7

export const validatePosition = (position: string) => position.length < 2
